import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Save,
  X,
  ExternalLink,
  Loader2,
  Plus,
  User,
  Phone,
  Zap,
  Search,
} from 'lucide-react'
import axios from 'axios'
import {
  companiesApi,
  qualificationApi,
  CompanyUpdateData,
  Contact,
} from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  PipelineStageBadge,
  PipelineStage,
  StageProgressDots,
} from '@/components/pipeline-stage-badge'
import { ProspectRelationshipBadge } from '@/components/prospect-relationship-badge'
import { CompanyPipelineStepper } from '@/components/company-pipeline-stepper'
import { useSetBreadcrumbLabel } from '@/contexts/BreadcrumbContext'

interface CompanyDetail {
  id: string
  name: string
  website: string | null
  address: string | null
  city: string | null
  country: string | null
  phone: string | null
  email: string | null
  company_email?: string | null
  company_emails?: string[]
  contact_person: string | null
  contact_title: string | null
  contacts?: Contact[]
  vat_number: string | null
  established_year: number | null
  employee_count: string | null
  business_type: string | null
  delivery_area: string | null
  payment_methods: string | null
  delivery_types: string | null
  industry: string | null
  company_type?: string | null
  company_type_tags?: string[]
  industry_tags?: string[]
  offering_tags?: string[]
  profile_summary?: string | null
  profile_freshness?: {
    last_profiled_at?: string | null
    stale_after?: string | null
    is_stale?: boolean
  } | null
  contact_availability?: {
    has_email?: boolean
    has_phone?: boolean
    global_contact_count?: number
    has_org_override?: boolean
  } | null
  source: string | null
  data_sources?: Array<{
    id: number
    source_type: 'scraper' | 'csv_import' | 'manual'
    source_name: string
    source_ref_id?: string | null
    contributed_fields?: string[]
    created_at: string
  }>
  stage: PipelineStage
  prospect_relationship?: string | null
  qualified?: { score?: number; qualification_reasons?: string[] | Record<string, unknown>; contact_enrichment_status?: string } | null
  hasAnalysis?: boolean
  created_at: string
  updated_at: string | null
  outreach?: {
    email_variables?: {
      personalizedIntro?: string
      specificBenefit?: string
      callToAction?: string
      companyName?: string
    }
    determined_role?: string
    sent?: boolean
  } | null
  latest_call?: {
    id: string
    status:
      | 'queued'
      | 'dialing'
      | 'in_progress'
      | 'completed'
      | 'failed'
      | 'no_answer'
      | 'busy'
      | 'cancelled'
    booking_status: 'not_attempted' | 'slots_offered' | 'booked' | 'failed' | 'declined'
    booked_start_at?: string | null
    booked_end_at?: string | null
    booked_timezone?: string | null
    summary?: string | null
    error?: string | null
  } | null
  calendar_ready_for_user?: boolean
  activities?: Array<{
    id: string
    activity_type: string
    channel?: string
    target_value?: string
    metadata?: Record<string, unknown>
    created_at: string
  }>
}

interface OutreachPreview {
  outreach: CompanyDetail['outreach']
  resolved_contact: {
    email: string | null
    phone: string | null
    name: string | null
    source: string
  }
}

interface FormField {
  key: keyof CompanyUpdateData
  label: string
  type?: 'text' | 'number' | 'url' | 'email'
}

const COMPANY_FORM_FIELDS: FormField[] = [
  { key: 'name', label: 'Company Name' },
  { key: 'website', label: 'Website', type: 'url' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'country', label: 'Country' },
  { key: 'vat_number', label: 'VAT Number' },
  { key: 'established_year', label: 'Established Year', type: 'number' },
  { key: 'employee_count', label: 'Employee Count' },
  { key: 'business_type', label: 'Business Type' },
  { key: 'delivery_area', label: 'Delivery Area' },
  { key: 'payment_methods', label: 'Payment Methods' },
  { key: 'delivery_types', label: 'Delivery Types' },
]

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setBreadcrumbLabel = useSetBreadcrumbLabel()
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<CompanyUpdateData>({})
  const [updateError, setUpdateError] = useState<string | null>(null)
  const [isAnalyzeInProgress, setIsAnalyzeInProgress] = useState(false)
  const [isGenerateOutreachInProgress, setIsGenerateOutreachInProgress] =
    useState(false)

  const { data, isLoading, error } = useQuery<CompanyDetail>({
    queryKey: ['company', id],
    queryFn: () => companiesApi.get(id!),
    enabled: !!id,
    refetchInterval:
      isAnalyzeInProgress || isGenerateOutreachInProgress ? 2_000 : false,
  })

  const { data: outreachPreview } = useQuery<OutreachPreview>({
    queryKey: ['company', id, 'outreach-preview'],
    queryFn: () => companiesApi.getOutreachPreview(id!),
    enabled: !!id,
  })

  const company = data

  useEffect(() => {
    if (company?.name) {
      setBreadcrumbLabel('companyName', company.name)
    }
    return () => setBreadcrumbLabel('companyName', undefined)
  }, [company?.name, setBreadcrumbLabel])

  // Clear analyze loading when analysis completes
  useEffect(() => {
    if (isAnalyzeInProgress && company?.hasAnalysis) {
      setIsAnalyzeInProgress(false)
    }
  }, [isAnalyzeInProgress, company?.hasAnalysis])

  // Clear generate-outreach loading when outreach appears
  useEffect(() => {
    if (isGenerateOutreachInProgress && company?.outreach) {
      setIsGenerateOutreachInProgress(false)
      queryClient.invalidateQueries({
        queryKey: ['company', id, 'outreach-preview'],
      })
    }
  }, [isGenerateOutreachInProgress, company?.outreach, queryClient, id])

  // Timeout to stop loading after 90s in case analysis never completes
  useEffect(() => {
    if (!isAnalyzeInProgress) return
    const timeout = setTimeout(() => setIsAnalyzeInProgress(false), 90_000)
    return () => clearTimeout(timeout)
  }, [isAnalyzeInProgress])

  // Timeout to stop loading after 90s in case outreach generation never completes
  useEffect(() => {
    if (!isGenerateOutreachInProgress) return
    const timeout = setTimeout(() => setIsGenerateOutreachInProgress(false), 90_000)
    return () => clearTimeout(timeout)
  }, [isGenerateOutreachInProgress])

  const updateMutation = useMutation({
    mutationFn: (data: CompanyUpdateData) => companiesApi.update(id!, data),
    onSuccess: (updatedData) => {
      // Update the cache with the new data immediately
      queryClient.setQueryData(['company', id], updatedData)
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      setUpdateError(null)
      setIsEditing(false)
    },
    onError: (error) => {
      console.error('Failed to update company:', error)
      const message = axios.isAxiosError(error)
        ? error.response?.data?.detail || error.message
        : error instanceof Error
          ? error.message
          : 'Failed to update company'
      setUpdateError(message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => companiesApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      navigate('/app/companies')
    },
  })

  const runPipelineMutation = useMutation({
    mutationFn: (options?: {
      sendEmail?: boolean
      step?: 'qualify' | 'contact' | 'analyze' | 'outreach'
    }) => companiesApi.runPipeline([id!], options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] })
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const qualifyMutation = useMutation({
    mutationFn: () => qualificationApi.qualify([id!]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] })
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const sendOutreachMutation = useMutation({
    mutationFn: () => companiesApi.sendOutreach(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] })
      queryClient.invalidateQueries({ queryKey: ['company', id, 'outreach-preview'] })
    },
  })

  const callMutation = useMutation({
    mutationFn: () => companiesApi.call(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] })
      queryClient.invalidateQueries({ queryKey: ['company', id, 'outreach-preview'] })
    },
  })

  const handleEdit = () => {
    if (company) {
      setUpdateError(null)
      const companyEmails =
        company.company_emails ||
        (company.company_email ? [company.company_email] : [])
      const contacts: Contact[] =
        company.contacts?.length ?
          company.contacts.map((c) => ({
            id: c.id,
            name: c.name ?? null,
            title: c.title ?? null,
            email: c.email ?? null,
            phone: c.phone ?? null,
            is_primary: c.is_primary ?? false,
          }))
        : []
      setFormData({
        name: company.name,
        website: company.website || undefined,
        address: company.address || undefined,
        city: company.city || undefined,
        country: company.country || undefined,
        company_emails: companyEmails,
        contacts,
        vat_number: company.vat_number || undefined,
        established_year: company.established_year || undefined,
        employee_count: company.employee_count || undefined,
        business_type: company.business_type || undefined,
        delivery_area: company.delivery_area || undefined,
        payment_methods: company.payment_methods || undefined,
        delivery_types: company.delivery_types || undefined,
      })
      setIsEditing(true)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setFormData({})
    setUpdateError(null)
  }

  const handleSave = () => {
    const payload: CompanyUpdateData = { ...formData }
    if (payload.company_emails) {
      payload.company_emails = payload.company_emails.filter((e) => e.trim() !== '')
    }
    if (payload.contacts) {
      payload.contacts = payload.contacts.map((c) => ({
        ...c,
        name: c.name?.trim() || null,
        title: c.title?.trim() || null,
        email: c.email?.trim() || null,
        phone: c.phone?.trim() || null,
      }))
    }
    updateMutation.mutate(payload)
  }

  const handleInputChange = (key: keyof CompanyUpdateData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [key]:
        key === 'established_year' ? (value ? parseInt(value, 10) : undefined) : value || undefined,
    }))
  }

  const handleCompanyEmailAdd = () => {
    setFormData((prev) => ({
      ...prev,
      company_emails: [...(prev.company_emails || []), ''],
    }))
  }

  const handleCompanyEmailChange = (index: number, value: string) => {
    setFormData((prev) => {
      const emails = [...(prev.company_emails || [])]
      emails[index] = value
      return { ...prev, company_emails: emails }
    })
  }

  const handleCompanyEmailRemove = (index: number) => {
    setFormData((prev) => {
      const emails = [...(prev.company_emails || [])]
      emails.splice(index, 1)
      return { ...prev, company_emails: emails }
    })
  }

  const handleContactAdd = () => {
    setFormData((prev) => ({
      ...prev,
      contacts: [...(prev.contacts || []), { name: null, title: null, email: null, phone: null, is_primary: false }],
    }))
  }

  const handleContactChange = (
    index: number,
    field: keyof Contact,
    value: string | boolean | null
  ) => {
    setFormData((prev) => {
      const contacts = [...(prev.contacts || [])]
      contacts[index] = { ...contacts[index], [field]: value }
      return { ...prev, contacts }
    })
  }

  const handleContactRemove = (index: number) => {
    setFormData((prev) => {
      const contacts = [...(prev.contacts || [])]
      contacts.splice(index, 1)
      return { ...prev, contacts }
    })
  }

  const getCompanyEmails = (): string[] =>
    company?.company_emails?.length ?
      company.company_emails
    : company?.company_email ?
      [company.company_email]
    : []

  const getContacts = (): Contact[] => company?.contacts ?? []

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="grid gap-4 md:grid-cols-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !company) {
    const errorMessage = axios.isAxiosError(error)
      ? error.response?.data?.detail || error.message
      : error instanceof Error
        ? error.message
        : null

    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/app/companies')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Companies
        </Button>
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">
              {errorMessage || 'Company not found'}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/companies')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{company.name}</h1>
            <div className="mt-1 flex items-center gap-3">
              <PipelineStageBadge stage={company.stage} size="sm" />
              {company.prospect_relationship && (
                <ProspectRelationshipBadge
                  relationship={company.prospect_relationship}
                  size="sm"
                />
              )}
              <StageProgressDots stage={company.stage} />
              {company.company_type && (
                <span className="text-sm rounded bg-primary/10 px-2 py-0.5 text-primary">
                  {company.company_type}
                </span>
              )}
              {company.industry && (
                <span className="text-sm text-muted-foreground">{company.industry}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancel} disabled={updateMutation.isPending}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleEdit}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Company</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete &quot;{company.name}&quot;? This action cannot
                      be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteMutation.mutate()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleteMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>
      {updateError ? (
        <p className="text-sm text-destructive">{updateError}</p>
      ) : null}

      {/* Pipeline Stepper */}
      <CompanyPipelineStepper
        company={company}
        outreachPreview={outreachPreview}
        onQualify={() => qualifyMutation.mutate()}
        onAnalyze={() => {
          setIsAnalyzeInProgress(true)
          runPipelineMutation.mutate(
            { step: 'analyze' },
            { onError: () => setIsAnalyzeInProgress(false) }
          )
        }}
        onFindContact={() => runPipelineMutation.mutate({ step: 'contact' })}
        onGenerateOutreach={() => {
          setIsGenerateOutreachInProgress(true)
          runPipelineMutation.mutate(
            { step: 'outreach' },
            { onError: () => setIsGenerateOutreachInProgress(false) }
          )
        }}
        onSendEmail={() => sendOutreachMutation.mutate()}
        onCall={() => callMutation.mutate()}
        isPipelinePending={
          runPipelineMutation.isPending ||
          isAnalyzeInProgress ||
          isGenerateOutreachInProgress
        }
        isAnalyzePending={isAnalyzeInProgress}
        isQualifyPending={qualifyMutation.isPending}
        isSendEmailPending={sendOutreachMutation.isPending}
        isCallPending={callMutation.isPending}
      />

      {/* Company Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Company Information</CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="grid gap-4 md:grid-cols-2">
              {COMPANY_FORM_FIELDS.map((field) => (
                <div key={field.key} className="space-y-2">
                  <Label htmlFor={field.key}>{field.label}</Label>
                  <Input
                    id={field.key}
                    type={field.type || 'text'}
                    value={formData[field.key]?.toString() || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                </div>
              ))}
              <div className="space-y-3 md:col-span-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Company Emails</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCompanyEmailAdd}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Email
                  </Button>
                </div>
                <div className="space-y-2">
                  {(formData.company_emails || []).map((email, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => handleCompanyEmailChange(idx, e.target.value)}
                        placeholder="company@example.com"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => handleCompanyEmailRemove(idx)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              <DetailItem label="Company Name" value={company.name} />
              <DetailItem
                label="Website"
                value={company.website}
                isLink
                href={company.website || undefined}
              />
              <DetailItem
                label="Company Emails"
                value={getCompanyEmails().filter(Boolean).join(', ') || undefined}
              />
              <DetailItem
                label="Company Type"
                value={company.company_type || undefined}
              />
              <DetailItem
                label="Type Tags"
                value={company.company_type_tags?.join(', ') || undefined}
              />
              <DetailItem
                label="Industry Tags"
                value={company.industry_tags?.join(', ') || undefined}
              />
              <DetailItem
                label="Offering Tags"
                value={company.offering_tags?.join(', ') || undefined}
              />
              <DetailItem
                label="Profile Freshness"
                value={company.profile_freshness?.last_profiled_at
                  ? `${company.profile_freshness.is_stale ? 'Stale' : 'Fresh'} (last profiled ${new Date(company.profile_freshness.last_profiled_at).toLocaleString()})`
                  : undefined}
              />
              <DetailItem
                label="Profile Summary"
                value={company.profile_summary || undefined}
              />
              <DetailItem label="Address" value={company.address} />
              <DetailItem label="City" value={company.city} />
              <DetailItem label="Country" value={company.country} />
              <DetailItem label="VAT Number" value={company.vat_number} />
              <DetailItem label="Established Year" value={company.established_year?.toString()} />
              <DetailItem label="Employee Count" value={company.employee_count} />
              <DetailItem label="Business Type" value={company.business_type} />
              <DetailItem label="Delivery Area" value={company.delivery_area} />
              <DetailItem label="Payment Methods" value={company.payment_methods} />
              <DetailItem label="Delivery Types" value={company.delivery_types} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact People Card */}
      <Card>
        <CardHeader>
          <CardTitle>Contact People</CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              {(formData.contacts || []).map((contact, idx) => (
                <div
                  key={idx}
                  className="flex flex-col gap-3 rounded-lg border p-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Contact {idx + 1}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleContactRemove(idx)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={contact.name ?? ''}
                        onChange={(e) =>
                          handleContactChange(idx, 'name', e.target.value || null)
                        }
                        placeholder="Contact name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        value={contact.title ?? ''}
                        onChange={(e) =>
                          handleContactChange(idx, 'title', e.target.value || null)
                        }
                        placeholder="Job title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={contact.email ?? ''}
                        onChange={(e) =>
                          handleContactChange(idx, 'email', e.target.value || null)
                        }
                        placeholder="email@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={contact.phone ?? ''}
                        onChange={(e) =>
                          handleContactChange(idx, 'phone', e.target.value || null)
                        }
                        placeholder="Phone number"
                      />
                    </div>
                    <div className="flex items-center gap-2 md:col-span-2">
                      <input
                        type="checkbox"
                        id={`contact-primary-${idx}`}
                        checked={contact.is_primary ?? false}
                        onChange={(e) =>
                          handleContactChange(idx, 'is_primary', e.target.checked)
                        }
                      />
                      <Label htmlFor={`contact-primary-${idx}`}>Primary contact</Label>
                    </div>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={handleContactAdd}>
                <Plus className="mr-2 h-4 w-4" />
                Add Contact
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {getContacts().length ? (
                getContacts().map((contact, idx) => (
                  <div
                    key={contact.id ?? idx}
                    className="rounded-lg border p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {contact.name || 'Unnamed Contact'}
                          {contact.is_primary && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                              Primary
                            </span>
                          )}
                        </p>
                        {contact.title && (
                          <p className="text-sm text-muted-foreground">{contact.title}</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 grid gap-2 text-sm md:grid-cols-2">
                      {contact.email && (
                        <DetailItem label="Email" value={contact.email} />
                      )}
                      {contact.phone && (
                        <DetailItem label="Phone" value={contact.phone} />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground">No contacts added yet.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Timeline Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Activity Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          {company.activities?.length ? (
            <ul className="space-y-3">
              {company.activities.map((a) => (
                <li key={a.id} className="flex gap-3 text-sm">
                  <ActivityIcon type={a.activity_type} />
                  <div>
                    <p className="font-medium capitalize">
                      {a.activity_type.replace(/_/g, ' ')}
                    </p>
                    {a.target_value && (
                      <p className="text-muted-foreground text-xs">{a.target_value}</p>
                    )}
                    <p className="text-muted-foreground text-xs">
                      {new Date(a.created_at).toLocaleString()}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground">No activities yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Metadata Card */}
      <Card>
        <CardHeader>
          <CardTitle>Record Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            <DetailItem
              label="Contact Availability"
              value={
                company.contact_availability
                  ? `${company.contact_availability.has_email ? 'Email' : ''}${company.contact_availability.has_email && company.contact_availability.has_phone ? ', ' : ''}${company.contact_availability.has_phone ? 'Phone' : ''}` || 'None'
                  : undefined
              }
            />
            <DetailItem
              label="Created At"
              value={new Date(company.created_at).toLocaleString()}
            />
            <DetailItem
              label="Last Updated"
              value={company.updated_at ? new Date(company.updated_at).toLocaleString() : null}
            />
          </div>

          {/* Data Sources */}
          <div className="mt-6">
            <h4 className="text-sm font-medium mb-3">Data Sources</h4>
            {company.data_sources && company.data_sources.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {company.data_sources.map((source) => (
                  <div
                    key={source.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted text-sm"
                  >
                    <span className="text-base">
                      {source.source_type === 'scraper' && '🌐'}
                      {source.source_type === 'csv_import' && '📄'}
                      {source.source_type === 'manual' && '✏️'}
                    </span>
                    <span className="font-medium">{source.source_name}</span>
                    <span className="text-muted-foreground text-xs">
                      {new Date(source.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : company.source ? (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted text-sm">
                <span className="text-base">🌐</span>
                <span className="font-medium">{company.source}</span>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No data source information available</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

const ACTIVITY_ICONS: Record<string, React.ReactNode> = {
  scraped: '📥',
  qualified: '✅',
  contact_found: '👤',
  analyzed: '🔍',
  website_analyzed: '🔍',
  outreach_generated: '✉️',
  email_sent: '📧',
  phone_call_queued: '🕓',
  phone_call_completed: '📞',
  phone_call_failed: '⚠️',
  phone_called: '📞',
  meeting_booked: '📅',
}

function ActivityIcon({ type }: { type: string }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-lg">
      {ACTIVITY_ICONS[type] ?? '•'}
    </span>
  )
}

interface DetailItemProps {
  label: string
  value: string | null | undefined
  isLink?: boolean
  href?: string
}

function DetailItem({ label, value, isLink, href }: DetailItemProps) {
  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      {value ? (
        isLink && href ? (
          <a
            href={href.startsWith('http') ? href : `https://${href}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 flex items-center gap-1 text-primary hover:underline"
          >
            {value}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <p className="mt-1 text-foreground">{value}</p>
        )
      ) : (
        <p className="mt-1 text-muted-foreground">-</p>
      )}
    </div>
  )
}
